import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import Layout from "./common/Layout";
import Loading from "./common/Loading";
import { get_account_details } from "../apis/GetAccountDetails";
import "../css/storeDetail.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";

const StoreDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 从路由参数获取账户ID

  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
  }, []);

  // 获取账户详情数据
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await get_account_details(id);

        if (response.data && response.data.data) {
          setStoreData(response.data.data);
        } else {
          setError(response.data?.message || "获取账户详情失败");
        }
      } catch (err) {
        console.error("获取账户详情失败:", err);
        setError(err.response?.data?.message || "获取账户详情失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAccountDetails();
    } else {
      setError("账户ID不存在");
      setLoading(false);
    }
  }, [id]);

  const handleBack = () => {
    navigate("/my-account");
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // 复制成功提示
        message.success("复制成功！");
      })
      .catch((err) => {
        console.error("复制失败:", err);
        message.error("复制失败，请手动复制");
      });
  };

  // 如果正在加载
  if (loading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  // 如果有错误
  if (error) {
    return (
      <Layout>
        <div className="store-detail">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => navigate("/my-account")}>返回列表</button>
          </div>
        </div>
      </Layout>
    );
  }

  // 如果没有数据
  if (!storeData) {
    return (
      <Layout>
        <div className="store-detail">
          <div className="error-message">
            <p>未找到账户详情</p>
            <button onClick={() => navigate("/my-account")}>返回列表</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="store-detail">
        {/* 头部标题 */}
        <div className="detail-header">
          <button className="btn-back" onClick={handleBack}>
            ←
          </button>
          <h1 className="page-title">账户详情</h1>
        </div>

        <div className="details_box">
          {/* 店铺基本信息 */}
          <div className="info-section">
            <div className="info-row">
              <span className="info-label">店铺名称：</span>
              <span className="info-value">
                {storeData.shop_name || storeData.platform_name || "-"}
              </span>
            </div>
          </div>

          {/* 账户余额卡片 */}
          <div className="balance-cards">
            <div className="balance-card blue">
              <div className="balance-amount">
                {storeData.available_balance || "0.00"}
              </div>
              <div className="balance-label">
                账户总金额 ({storeData.currency || "GBP"})
              </div>
            </div>
          </div>

          {/* 收款账户信息 */}
          <div className="section-header">收款账户信息</div>
          <div className="bank-info-grid">
            <div className="info-item">
              <div className="info-label">
                银行所在地 (Bank Location Country)
              </div>
              <div className="info-value">
                {storeData.bank_location_country || "-"}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">银行账号 (Bank Account Number)</div>
              <div className="info-value copy-value">
                {storeData.actual_number || "-"}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">国际银行账户号码 (IBAN)</div>
              <div className="info-value copy-value">
                {storeData.iban || "-"}
                {storeData.iban && (
                  <button
                    className="btn-copy"
                    onClick={() => handleCopy(storeData.iban)}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">银行分类代码 (Bank Sort Code)</div>
              <div className="info-value copy-value">
                {storeData.bank_sort_code || "-"}
                {storeData.bank_sort_code && (
                  <button
                    className="btn-copy"
                    onClick={() => handleCopy(storeData.bank_sort_code)}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">BIC Code</div>
              <div className="info-value copy-value">
                {storeData.bic_code || "-"}
                {storeData.bic_code && (
                  <button
                    className="btn-copy"
                    onClick={() => handleCopy(storeData.bic_code)}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">
                账户币种 (Account Currency)
                <span className="info-help">?</span>
              </div>
              <div className="info-value">{storeData.currency || "-"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">银行名称 (Bank Name)</div>
              <div className="info-value copy-value">
                {storeData.bank_name || "-"}
                {storeData.bank_name && (
                  <button
                    className="btn-copy"
                    onClick={() => handleCopy(storeData.bank_name)}
                  >
                    📋
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 店铺信息表格 */}
          <div className="section-header">店铺信息</div>
          <div className="table-container">
            <table className="store-info-table">
              <thead>
                <tr>
                  <th>站点名称</th>
                  <th>店铺名称</th>
                  <th>卖家编号</th>
                  <th>店铺链接</th>
                  <th>币种</th>
                  <th>状态</th>
                  <th>备注</th>
                </tr>
              </thead>
              <tbody>
                {storeData ? (
                  <tr>
                    <td>{storeData.platform_name || "-"}</td>
                    <td>{storeData.shop_name || "-"}</td>
                    <td>-</td>
                    <td>
                      {storeData.shop_url ? (
                        <a
                          href={storeData.shop_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {storeData.shop_url}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{storeData.currency || "-"}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          storeData.account_status === "pending"
                            ? "status-warning"
                            : storeData.account_status === "active"
                            ? "status-success"
                            : "status-warning"
                        }`}
                      >
                        ● {storeData.account_status || "-"}
                      </span>
                    </td>
                    <td>{storeData.remark || "-"}</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>
                      暂无店铺信息
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="page-footer" style={{ padding: "20px 0" }}>
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </Layout>
  );
};

export default StoreDetail;
